import React, { useState } from 'react';
import Button from '../common/Button';
import { apiService } from '../../services/api';

export interface RubricCriterion {
  id: number;
  criterion_key: string;
  label: string;
  description: string;
  guidance: Record<string, string>;
  weight: number | string;
}

export interface CompetitionRubric {
  id: number;
  version: number;
  title: string;
  criteria: RubricCriterion[];
}

interface Props {
  competitionId: number;
  rubric: CompetitionRubric;
  submission: {
    submission_id: number;
    submission_version: number;
    already_scored?: boolean;
    criterion_scores?: { criterion_id: number; score: number }[];
    reviewed_submission_version?: number;
    evaluation_feedback?: string | null;
  } | null;
  remaining: number;
  feedbackRequired: boolean;
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
  onSubmitted: () => Promise<void>;
  onNext: () => void;
}

const RubricScoreForm: React.FC<Props> = ({ competitionId, rubric, submission, remaining, feedbackRequired, disabled = false, onBusyChange, onSubmitted, onNext }) => {
  const [scores, setScores] = useState<Record<number, number>>(() => Object.fromEntries(
    (submission?.criterion_scores || []).map((item) => [item.criterion_id, item.score]),
  ));
  const [feedback, setFeedback] = useState(submission?.evaluation_feedback || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<number | null>(null);
  const complete = rubric.criteria.length > 0 && rubric.criteria.every((item) => Number.isInteger(scores[item.id]) && scores[item.id] >= 1 && scores[item.id] <= 5);
  const readOnly = disabled || !submission || Boolean(submission.already_scored) || result !== null || remaining <= 0 || busy;
  const overall = complete ? rubric.criteria.reduce((total, item) => total + scores[item.id] * Number(item.weight), 0)
    / rubric.criteria.reduce((total, item) => total + Number(item.weight), 0) : null;

  const submit = async () => {
    if (readOnly || !complete || !submission) return;
    setBusy(true);
    onBusyChange?.(true);
    setError('');
    try {
      const response = await apiService.post(`/evaluations/rubric/${competitionId}/evaluations`, {
        rubric_id: rubric.id,
        submission_id: submission.submission_id,
        submission_version: submission.submission_version,
        scores: rubric.criteria.map((item) => ({ criterion_id: item.id, score: scores[item.id] })),
        feedback: feedback.trim() || null,
      });
      setResult(Number(response.data.overall_score));
      // A successful submission remains successful if refreshing the list fails.
      try { await onSubmitted(); } catch { setError('Evaluation saved. Refresh to update your remaining allowance.'); }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not submit the evaluation.');
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  };

  return <div className="evaluations-rubric-form">
    <h3>{rubric.title}</h3>
    <p>Score each criterion from 1 to 5. Submit once per team or participant.</p>
    {rubric.criteria.map((item) => <fieldset key={item.id} disabled={readOnly} className="evaluations-rubric-criterion">
      <legend>{item.label}</legend>
      <p>{item.description}</p>
      <div className="evaluations-rubric-scale">
        {[1, 2, 3, 4, 5].map((value) => <label key={value}>
          <input type="radio" name={`criterion-${item.id}`} value={value} checked={scores[item.id] === value}
            onChange={() => setScores((previous) => ({ ...previous, [item.id]: value }))} />
          <strong>{value}</strong><span>{item.guidance[String(value)]}</span>
        </label>)}
      </div>
    </fieldset>)}
    {overall !== null && <p>Overall score: <strong>{overall.toFixed(2)} / 5</strong></p>}
    <label htmlFor="rubric-feedback">Feedback {feedbackRequired ? '(required)' : '(optional)'}</label>
    <textarea id="rubric-feedback" rows={3} maxLength={1000} value={feedback} disabled={readOnly}
      onChange={(event) => setFeedback(event.target.value)} />
    {submission?.already_scored && <p>You already evaluated this team. Reviewed submission version: {submission.reviewed_submission_version}.</p>}
    {!remaining && <p>You have used your evaluation allowance. Completed projects remain available for review.</p>}
    {error && <p role="alert" className="evaluations-error">{error}</p>}
    {result !== null ? <>
      <p role="status">Evaluation saved: <strong>{result.toFixed(2)} / 5</strong></p>
      <Button onClick={onNext}>Next project</Button>
    </> : <Button onClick={submit} disabled={readOnly || !complete || (feedbackRequired && feedback.trim().length < 10)}>
      {busy ? 'Submitting...' : 'Submit evaluation'}
    </Button>}
  </div>;
};

export default RubricScoreForm;
